import { observabilityFetch } from './backoffice-api';

export type ObservabilityRange = '15m' | '1h' | '6h' | '24h';
export type TopologyNode = {
  id: string;
  label: string;
  kind: 'service' | 'topic' | 'external';
  evidence?: 'configured' | 'observed';
  health: 'healthy' | 'degraded' | 'unhealthy' | 'unknown';
  requestRate: number;
  errorRate: number;
  p95LatencyMs: number;
};
export type TopologyEdge = {
  id: string;
  source: string;
  target: string;
  protocol: 'http' | 'publish' | 'consume' | 'beacon' | 'websocket' | 'webrtc';
  evidence?: 'configured' | 'observed';
  trafficType?: 'application' | 'telemetry';
  activity?: {
    attempts: number;
    opened: number;
    failures: number;
    closed: number;
    abnormal: number;
    queued: number;
    rejected: number;
    lastSeen?: string;
    p95SetupMs?: number;
    p95DurationMs?: number;
  };
  requestRate: number;
  errorRate: number;
  p95LatencyMs: number;
};
export type Topology = {
  generatedAt: string;
  environment: 'dev' | 'prod';
  range: ObservabilityRange;
  nodes: TopologyNode[];
  edges: TopologyEdge[];
};

export async function getTopology(range: ObservabilityRange): Promise<Topology> {
  const params = new URLSearchParams({ range });
  const response = await observabilityFetch(`/topology?${params}`);
  const body: unknown = await response.json();
  if (
    response.status === 503 &&
    typeof body === 'object' &&
    body &&
    'code' in body &&
    body.code === 'TOPOLOGY_LIMIT_EXCEEDED'
  )
    throw new TopologyLimitError(
      'Architecture data exceeds the safety limit. Choose a shorter time range or contact an administrator.'
    );
  if (!response.ok)
    throw new Error(
      typeof body === 'object' && body && 'error' in body
        ? String(body.error)
        : 'Unable to load architecture topology'
    );
  return body as Topology;
}

export class TopologyLimitError extends Error {
  readonly code = 'TOPOLOGY_LIMIT_EXCEEDED';
}
export function isTopologyLimitError(error: unknown): error is TopologyLimitError {
  return error instanceof TopologyLimitError;
}

export type DestinationIdentity = {
  caller: string;
  range: ObservabilityRange;
  generatedAt: string;
  truncated: boolean;
  samples: Array<{
    hostname: string | null;
    category: 'http' | 'dns' | 'messaging' | 'other';
    count: number;
    lastSeen: string;
  }>;
};

export async function getDestinationIdentity(
  caller: string,
  range: ObservabilityRange,
  signal?: AbortSignal
): Promise<DestinationIdentity> {
  const response = await observabilityFetch(
    `/destination-identity?${new URLSearchParams({ caller, range })}`,
    signal ? { signal } : {}
  );
  if (!response.ok) throw new Error('Destination identity details are temporarily unavailable.');
  return response.json();
}
